import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@journ/ui/accordion'
import { Avatar, AvatarFallback, AvatarImage } from '@journ/ui/avatar'
import { Button } from '@journ/ui/button'
import { Input } from '@journ/ui/input'
import { Label } from '@journ/ui/label'
import { SubmitButton } from '@journ/ui/submit-button'
import { Textarea } from '@journ/ui/textarea'
import { useEffect, useRef, useState } from 'react'
import { useIntlayer } from 'react-intlayer'
import { z } from '@/libs/zod'
import type { Customer, CustomerKind } from '../domain/customer'

const schema = z.object({
	kind: z.enum(['person', 'company']),
	name: z.string().min(2),
	email: z.email(),
	taxId: z.string().optional(),
	phone: z.string().optional(),
	website: z
		.string()
		.optional()
		.transform(v => v?.replace(/^https?:\/\//, '') || undefined),
	contact: z.string().optional(),
	addressLine1: z.string().optional(),
	addressLine2: z.string().optional(),
	city: z.string().optional(),
	state: z.string().optional(),
	country: z.string().optional(),
	countryCode: z.string().optional(),
	zip: z.string().optional(),
	note: z.string().optional(),
})

type FormValues = z.infer<typeof schema>

type SubmitValues = FormValues & { id?: string; logo?: File | null }

type Props = {
	data?: Customer
	onSubmit: (values: SubmitValues) => Promise<void>
	onCancel: () => void
}

const field = (label: string, children: React.ReactNode, error?: string) => (
	<div className='space-y-1.5'>
		<Label className='text-xs text-878787 font-normal'>{label}</Label>
		{children}
		{error && <p className='text-xs text-destructive'>{error}</p>}
	</div>
)

const CustomerForm = ({ data, onSubmit, onCancel }: Props) => {
	const content = useIntlayer('customers-page')
	const isEdit = !!data

	const [kind, setKind] = useState<CustomerKind>(data?.kind ?? 'company')
	const [values, setValues] = useState<Record<string, string>>({
		name: data?.name ?? '',
		email: data?.email ?? '',
		taxId: data?.taxId ?? '',
		phone: data?.phone ?? '',
		website: data?.website ?? '',
		contact: data?.contact ?? '',
		addressLine1: data?.addressLine1 ?? '',
		addressLine2: data?.addressLine2 ?? '',
		city: data?.city ?? '',
		state: data?.state ?? '',
		country: data?.country ?? '',
		countryCode: data?.countryCode ?? '',
		zip: data?.zip ?? '',
		note: data?.note ?? '',
	})
	const [errors, setErrors] = useState<Partial<Record<string, string>>>({})
	const [submitting, setSubmitting] = useState(false)
	const [logoFile, setLogoFile] = useState<File | null>(null)
	const [logoPreview, setLogoPreview] = useState<string | null>(data?.logoUrl ?? null)
	const fileInputRef = useRef<HTMLInputElement>(null)

	useEffect(() => {
		if (!logoFile) return
		const url = URL.createObjectURL(logoFile)
		setLogoPreview(url)
		return () => URL.revokeObjectURL(url)
	}, [logoFile])

	const set = (key: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
		setValues(v => ({ ...v, [key]: e.target.value }))

	const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setLogoFile(e.target.files?.[0] ?? null)
	}

	const handleRemoveLogo = () => {
		setLogoFile(null)
		setLogoPreview(null)
		if (fileInputRef.current) fileInputRef.current.value = ''
	}

	const handleSubmit = async (e: React.SubmitEvent<HTMLFormElement>) => {
		e.preventDefault()
		const result = schema.safeParse({ ...values, kind })
		if (!result.success) {
			const flat = z.flattenError(result.error).fieldErrors
			setErrors(Object.fromEntries(Object.entries(flat).map(([k, v]) => [k, v?.[0]])))
			return
		}
		setErrors({})
		setSubmitting(true)
		try {
			// Pass logo as File when a new one was selected, null when the user
			// explicitly cleared it, or undefined when it was not touched.
			const logo = logoFile ?? (logoPreview === null && data?.logoUrl ? null : undefined)
			await onSubmit({ ...result.data, id: data?.id, logo })
		} finally {
			setSubmitting(false)
		}
	}

	const taxIdPlaceholder =
		kind === 'company' ? content.form.taxIdCompanyPlaceholder.value : content.form.taxIdPersonPlaceholder.value

	const avatarFallback = (values.name?.[0] ?? '?').toUpperCase()

	return (
		<form onSubmit={handleSubmit} className='flex flex-col min-h-0 flex-1'>
			<div className='flex-1 min-h-0 overflow-auto'>
				<Accordion type='multiple' defaultValue={['general']} className='space-y-6'>
					<AccordionItem value='general'>
						<AccordionTrigger>{content.form.general}</AccordionTrigger>
						<AccordionContent>
							<div className='space-y-4'>
								{/* Kind toggle */}
								<div className='space-y-1.5'>
									<Label className='text-xs text-878787 font-normal'>{content.form.kind}</Label>
									<div className='flex gap-2'>
										{(['company', 'person'] as const).map(k => (
											<button
												key={k}
												type='button'
												onClick={() => setKind(k)}
												className={`flex-1 rounded-md border px-4 py-2 text-sm transition-colors ${
													kind === k
														? 'border-foreground bg-foreground text-background'
														: 'border-border bg-background text-foreground hover:bg-muted/40'
												}`}
											>
												{k === 'company' ? content.form.kindCompany : content.form.kindPerson}
											</button>
										))}
									</div>
								</div>

								{/* Logo */}
								<div className='space-y-1.5'>
									<Label className='text-xs text-878787 font-normal'>{content.form.logo}</Label>
									<div className='flex items-center gap-3'>
										<Avatar className='size-12 rounded-md'>
											<AvatarImage src={logoPreview ?? ''} alt='' className='object-cover' />
											<AvatarFallback className='rounded-md text-sm font-medium'>{avatarFallback}</AvatarFallback>
										</Avatar>
										<div className='flex flex-col gap-1'>
											<button
												type='button'
												onClick={() => fileInputRef.current?.click()}
												className='text-xs text-primary hover:underline text-left'
											>
												{logoPreview ? content.form.logo : content.form.logo}
											</button>
											{logoPreview && (
												<button
													type='button'
													onClick={handleRemoveLogo}
													className='text-xs text-destructive hover:underline text-left'
												>
													Remove
												</button>
											)}
											<span className='text-[11px] text-muted-foreground'>{content.form.logoHint}</span>
										</div>
									</div>
									<input
										ref={fileInputRef}
										type='file'
										accept='image/jpeg,image/png,image/webp,image/svg+xml'
										onChange={handleLogoChange}
										className='sr-only'
									/>
								</div>

								{field(
									content.form.name.value,
									<Input
										value={values.name}
										onChange={set('name')}
										autoFocus
										placeholder={content.form.namePlaceholder.value}
										autoComplete='off'
									/>,
									errors.name,
								)}
								{field(
									content.form.email.value,
									<Input
										type='email'
										value={values.email}
										onChange={set('email')}
										placeholder={content.form.emailPlaceholder.value}
										autoComplete='off'
									/>,
									errors.email,
								)}
								{field(
									content.form.taxId.value,
									<Input
										value={values.taxId}
										onChange={set('taxId')}
										placeholder={taxIdPlaceholder}
										autoComplete='off'
									/>,
								)}
								{field(
									content.form.phone.value,
									<Input
										type='tel'
										value={values.phone}
										onChange={set('phone')}
										placeholder={content.form.phonePlaceholder.value}
										autoComplete='off'
									/>,
								)}
								{field(
									content.form.website.value,
									<Input
										value={values.website}
										onChange={set('website')}
										placeholder={content.form.websitePlaceholder.value}
										autoComplete='off'
									/>,
								)}
								{field(
									content.form.contactPerson.value,
									<Input
										value={values.contact}
										onChange={set('contact')}
										placeholder={content.form.contactPlaceholder.value}
										autoComplete='off'
									/>,
								)}
							</div>
						</AccordionContent>
					</AccordionItem>

					<AccordionItem value='details'>
						<AccordionTrigger>{content.form.details}</AccordionTrigger>
						<AccordionContent>
							<div className='space-y-4'>
								{field(
									content.form.addressLine1.value,
									<Input
										value={values.addressLine1}
										onChange={set('addressLine1')}
										placeholder={content.form.addressLine1Placeholder.value}
										autoComplete='off'
									/>,
								)}
								{field(
									content.form.addressLine2.value,
									<Input
										value={values.addressLine2}
										onChange={set('addressLine2')}
										placeholder={content.form.addressLine2Placeholder.value}
										autoComplete='off'
									/>,
								)}
								<div className='grid grid-cols-2 gap-4'>
									{field(
										content.form.country.value,
										<Input
											value={values.country}
											onChange={set('country')}
											placeholder={content.form.countryPlaceholder.value}
											autoComplete='off'
										/>,
									)}
									{field(
										content.form.city.value,
										<Input
											value={values.city}
											onChange={set('city')}
											placeholder={content.form.cityPlaceholder.value}
											autoComplete='off'
										/>,
									)}
								</div>
								<div className='grid grid-cols-2 gap-4'>
									{field(
										content.form.state.value,
										<Input
											value={values.state}
											onChange={set('state')}
											placeholder={content.form.statePlaceholder.value}
											autoComplete='off'
										/>,
									)}
									{field(
										content.form.zip.value,
										<Input
											value={values.zip}
											onChange={set('zip')}
											placeholder={content.form.zipPlaceholder.value}
											autoComplete='off'
										/>,
									)}
								</div>
								{field(
									content.form.note.value,
									<Textarea
										value={values.note}
										onChange={set('note')}
										className='min-h-20 resize-none'
										placeholder={content.form.notePlaceholder.value}
										autoComplete='off'
									/>,
								)}
							</div>
						</AccordionContent>
					</AccordionItem>
				</Accordion>
			</div>

			<div className='shrink-0 flex justify-end space-x-4 pt-4'>
				<Button variant='outline' onClick={onCancel} type='button'>
					{content.form.cancel}
				</Button>
				<SubmitButton isSubmitting={submitting}>{isEdit ? content.form.update : content.form.create}</SubmitButton>
			</div>
		</form>
	)
}

export { CustomerForm }
export type { FormValues, SubmitValues }
